import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import { PhLock } from 'ember-phosphor-icons';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import {
  parseReducedHeldAssistance,
  reduceHeldAssistanceRequest,
  reductionFailure,
  type HeldAssistance,
  type HeldFundsWindow,
} from 'tio-data/requests/held-funds';
import { formatAsDollars, toLocaleDateString } from '../../../utils/format.ts';
import { labelFor, LIMIT_LEVEL_KEYS } from '../../../utils/approval-decision-blurb.ts';
import {
  applicationStateIntent,
  freedByReduction,
  groupByApplication,
  heldFundsTotals,
  reductionProblem,
} from '../../../utils/tuition-assistance/held-funds.ts';
import type IntlService from 'ember-intl/services/intl';
import type { Store } from '@warp-drive/core';
import type {
  LimitsDetailSignature,
  LimitsStatusDataSignature,
} from '../../../services/tuition-assistance.ts';

interface HeldFundsModalSignature {
  Args: {
    isOpen: boolean;
    /** Fires on Done, Escape or the close button; refused while a save is in flight. */
    onClose: () => void;
    /** The application being approved — the one a reduction makes room for. */
    applicationId: string;
    /** The held-funds list as the server returned it; replaced wholesale on every reload. */
    rows: HeldAssistance[];
    /** Must match the window the page's limits status was read in. */
    window: HeldFundsWindow;
    /** The page's limits status for the application: the summary strip reads from it. */
    limits: LimitsStatusDataSignature | undefined;
    /** Shown in the subtitle beside the company and program year; omitted when absent. */
    employeeName?: string;
    /** Shown in the subtitle; omitted when absent. */
    companyName?: string;
    /** Remaining benefit is being re-read while the ledger catches up with a reduction. */
    isRefreshing?: boolean;
    /**
     * Fires after a successful reduce with the hold as the server now reports it and the
     * cents the release freed for this application.
     */
    onReduced: (row: HeldAssistance, freed: number) => void;
    /** Fires when a save finds the hold changed underneath, so the list can be re-read. */
    onStale?: () => void;
  };
}

// Commas only as thousands separators: a decimal comma ("12,50") must not read as 1,250.
const DOLLARS = /^(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{0,2})?$/;

// Blank, negative or over-precise input parses to NaN, which reductionProblem refuses.
const toCents = (input: string) => {
  const trimmed = input.trim().replace(/^\$/, '');
  return DOLLARS.test(trimmed) ? Math.round(Number(trimmed.replace(/,/g, '')) * 100) : NaN;
};

const NOT_RESERVED_REASON: Record<string, string> = {
  'TAS.AssistanceState.PAYABLE': 'payable',
  'TAS.AssistanceState.REPAYABLE': 'repayable',
  'TAS.AssistanceState.AUTHORIZED': 'authorized',
};

const HOLD_STATE_KEY: Record<string, string> = {
  'TAS.AssistanceState.UNSETTLED': 'unsettled',
  'TAS.AssistanceState.RESERVED': 'reserved',
  'TAS.AssistanceState.AUTHORIZED': 'authorized',
  'TAS.AssistanceState.PAYABLE': 'payable',
  'TAS.AssistanceState.REPAYABLE': 'repayable',
};

class HeldFundsModal extends Component<HeldFundsModalSignature> {
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked editingId: string | null = null;
  @tracked reductionInput = '';
  @tracked reasonInput = '';
  @tracked showProblems = false;
  @tracked saveError: string | null = null;
  // The change is checked back to the approver before anything is sent.
  @tracked isConfirming = false;
  // The row whose Reduce button takes focus back when its editor closes.
  @tracked returnFocusId: string | null = null;
  @tracked reduced: Record<string, number> = {};

  get groups() {
    return groupByApplication(this.args.rows);
  }

  get subtitle() {
    const { employeeName, companyName, limits } = this.args;
    const year =
      limits?.annual_start_date && limits.annual_end_date
        ? this.intl.t('common.tas.approval.held_funds.program_year', {
            start: toLocaleDateString(limits.annual_start_date),
            end: toLocaleDateString(limits.annual_end_date),
          })
        : '';
    return [employeeName, companyName, year].filter(Boolean).join(' · ');
  }

  // The level whose limit binds this application, e.g. `scheme_annual`; null when none does.
  get bindingLimit(): { label: string; detail: LimitsDetailSignature; annual: boolean } | null {
    const level = this.args.limits?.soft_limit;
    if (!level || level === 'none') return null;
    const annual = level.endsWith('_annual');
    const detail =
      this.args.limits?.details?.[
        level.replace('_annual', '') as keyof LimitsStatusDataSignature['details']
      ];
    if (!detail) return null;
    return { label: labelFor(this.intl, LIMIT_LEVEL_KEYS, level), detail, annual };
  }

  get limitAmount() {
    const binding = this.bindingLimit;
    const cents = binding && (binding.annual ? binding.detail.annual_limit : binding.detail.limit);
    return typeof cents === 'number' ? formatAsDollars(cents) : null;
  }

  get usedElsewhere() {
    const binding = this.bindingLimit;
    if (!binding) return null;
    return formatAsDollars(binding.annual ? binding.detail.annual_usage : binding.detail.usage);
  }

  get heldElsewhere() {
    return formatAsDollars(heldFundsTotals(this.args.rows).total);
  }

  get remaining() {
    const available = this.args.limits?.available;
    if (available === null) return this.intl.t('common.tas.approval.held_funds.unlimited');
    return typeof available === 'number' ? formatAsDollars(available) : '—';
  }

  get isOverLimit() {
    const available = this.args.limits?.available;
    return typeof available === 'number' && available < 0;
  }

  // Other holds' freeable amounts are stale until the ledger shows the last release.
  get isBusy() {
    return this.save.isRunning || !!this.args.isRefreshing;
  }

  // One edit at a time: switching rows would silently drop what was typed.
  get isReduceBlocked() {
    return this.isBusy || !!this.editingRow;
  }

  cardClass = (rows: HeldAssistance[]) => {
    const editing = this.editingRow;
    if (!editing) return '';
    return rows.includes(editing) ? 'held-funds-card--active' : 'held-funds-card--muted';
  };

  // Resolved against the current rows, so a reload that drops the hold or locks it ends the edit.
  get editingRow() {
    return this.args.rows.find((row) => row.assistanceId === this.editingId && row.reducible);
  }

  // A save error whose row a reload has since removed still needs saying.
  get orphanedSaveError() {
    return this.editingRow ? null : this.saveError;
  }

  // The approver types how much to take off; the hold's new amount follows from it.
  get proposedAmount() {
    const row = this.editingRow;
    return row ? row.amount - toCents(this.reductionInput) : NaN;
  }

  get newAmountLabel() {
    return this.reductionInput.trim() && this.problem !== 'amount'
      ? formatAsDollars(this.proposedAmount)
      : null;
  }

  get currentAmountLabel() {
    return formatAsDollars(this.editingRow?.amount ?? 0);
  }

  // Shown as soon as the reduction is typed, so an over-large figure never reaches Save.
  get showAmountProblem() {
    return this.problem === 'amount' && (this.showProblems || !!this.reductionInput.trim());
  }

  get frees() {
    const row = this.editingRow;
    return row ? freedByReduction(row, this.proposedAmount) : 0;
  }

  get problem() {
    const row = this.editingRow;
    return row ? reductionProblem(row, this.proposedAmount, this.reasonInput) : null;
  }

  get amountErrors() {
    return this.showAmountProblem
      ? [
          this.intl.t('common.tas.approval.held_funds.reduction_invalid', {
            max: this.currentAmountLabel,
          }),
        ]
      : [];
  }

  get confirmMessage() {
    const row = this.editingRow;
    if (!row) return '';
    return this.intl.t('common.tas.approval.held_funds.confirm_body', {
      manner: this.mannerLabel(row),
      application: this.applicationName(row),
      from: this.currentAmountLabel,
      to: formatAsDollars(this.proposedAmount),
    });
  }

  get lowersCardSpend() {
    return this.editingRow?.manner === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isSaveBlocked() {
    return !!this.args.isRefreshing || this.showAmountProblem;
  }

  get reasonErrors() {
    return this.showProblems && this.problem === 'reason'
      ? [this.intl.t('common.tas.approval.held_funds.reason_required')]
      : [];
  }

  applicationName = (group: { applicationName: string | null; applicationId: string }) =>
    group.applicationName ??
    this.intl.t('common.tas.approval.held_funds.application_fallback', { id: group.applicationId });

  applicationStateLabel = (state: string) => {
    const key = `common.status_message.to.${state}`;
    return this.intl.exists(key) ? this.intl.t(key) : (state.split('.').pop() ?? state);
  };

  stateIntent = (state: string) => applicationStateIntent(state);

  isCourseCashApplication = (rows: HeldAssistance[]) =>
    rows.some((row) => row.manner === 'TAS.AssistanceManner.COURSE_CASH');

  mannerLabel = (row: HeldAssistance) =>
    this.intl.t(
      row.manner === 'TAS.AssistanceManner.COURSE_CASH'
        ? 'common.tas.approval.held_funds.manner_course_cash'
        : 'common.tas.approval.held_funds.manner_reimbursement'
    );

  holdStateLabel = (row: HeldAssistance) => {
    const key = HOLD_STATE_KEY[row.state];
    return key
      ? this.intl.t(`common.tas.approval.held_funds.hold_state.${key}`)
      : (row.state.split('.').pop() ?? row.state);
  };

  blockedReason = (row: HeldAssistance) => {
    if (row.reasonCode === 'NO_SHARED_LIMIT') {
      return this.intl.t('common.tas.approval.held_funds.blocked.no_shared_limit');
    }
    const reason = NOT_RESERVED_REASON[row.state] ?? 'not_reserved';
    return this.intl.t(`common.tas.approval.held_funds.blocked.${reason}`);
  };

  // A bare ISO date parses as UTC midnight, so format it in UTC or it lands a day early.
  heldOn = (row: HeldAssistance) =>
    row.heldDate
      ? this.intl.formatDate(row.heldDate, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : '—';

  wasReduced = (row: HeldAssistance) => this.reduced[row.assistanceId] !== undefined;

  reducedFrom = (row: HeldAssistance) => formatAsDollars(this.reduced[row.assistanceId] ?? 0);

  isEditing = (row: HeldAssistance) => this.editingRow?.assistanceId === row.assistanceId;

  isReturnTarget = (row: HeldAssistance) => this.returnFocusId === row.assistanceId;

  reduceLabel = (row: HeldAssistance) =>
    this.intl.t('common.tas.approval.held_funds.reduce_label', {
      manner: this.mannerLabel(row),
      application: this.applicationName(row),
    });

  // Editor controls replace the button that opened them, so focus is moved on purpose.
  focusWhen = modifier((element: HTMLElement, [when]: [boolean]) => {
    if (when) element.focus();
  });

  startEditing = (row: HeldAssistance) => {
    this.editingId = row.assistanceId;
    this.reductionInput = '';
    this.reasonInput = '';
    this.showProblems = false;
    this.saveError = null;
    this.isConfirming = false;
  };

  cancelEditing = () => {
    this.returnFocusId = this.editingId;
    this.editingId = null;
    this.saveError = null;
    this.isConfirming = false;
  };

  review = () => {
    this.showProblems = true;
    if (this.problem || this.args.isRefreshing) return;
    this.saveError = null;
    this.isConfirming = true;
  };

  backToEditing = () => {
    this.isConfirming = false;
  };

  setReduction = (value: string) => {
    this.reductionInput = value;
  };

  setReason = (value: string) => {
    this.reasonInput = value;
  };

  close = () => {
    if (this.save.isRunning) return;
    this.cancelEditing();
    this.args.onClose();
  };

  save = dropTask(async () => {
    const row = this.editingRow;
    if (!row || !this.isConfirming || this.args.isRefreshing || this.problem) return;

    this.saveError = null;
    const freed = this.frees;
    try {
      const { content } = await this.store.request(
        reduceHeldAssistanceRequest(
          row.assistanceId,
          {
            applicationId: this.args.applicationId,
            amount: this.proposedAmount,
            reason: this.reasonInput.trim(),
          },
          this.args.window
        )
      );
      const updated = parseReducedHeldAssistance(
        content as Parameters<typeof parseReducedHeldAssistance>[0]
      );
      this.reduced = { ...this.reduced, [row.assistanceId]: row.amount };
      this.returnFocusId = row.assistanceId;
      this.editingId = null;
      this.isConfirming = false;
      this.args.onReduced(updated, freed);
    } catch (error) {
      const failure = reductionFailure(error);
      this.isConfirming = false;
      if (failure.status === 409) this.args.onStale?.();
      this.saveError =
        failure.status === 409
          ? this.intl.t('common.tas.approval.held_funds.conflict')
          : (failure.detail ?? this.intl.t('common.tas.approval.held_funds.save_failed'));
    }
  });

  <template>
    <Modal
      @isOpen={{@isOpen}}
      @onClose={{this.close}}
      @size="5xl"
      @ariaLabel={{t "common.tas.approval.held_funds.title"}}
      data-test-held-funds-modal
    >
      <:header>
        <div class="pr-6">
          <div>{{t "common.tas.approval.held_funds.title"}}</div>
          <div class="tio-text-sm-normal tio-fg-muted mt-1" data-test-held-funds-subtitle>
            {{this.subtitle}}
            {{#if @isRefreshing}}
              ·
              <span data-test-held-funds-refreshing>{{t
                  "common.tas.approval.held_funds.refreshing"
                }}</span>
            {{/if}}
          </div>
        </div>
      </:header>
      <:body>
        {{#if this.orphanedSaveError}}
          <p class="tio-text-sm-normal tio-fg-danger mb-3" role="alert" data-test-held-funds-error>
            {{this.orphanedSaveError}}
          </p>
        {{/if}}
        <dl class="held-funds-summary" data-test-held-funds-summary>
          {{#if this.limitAmount}}
            <div>
              <dt>{{t
                  "common.tas.approval.held_funds.summary.limit"
                  level=this.bindingLimit.label
                }}</dt>
              <dd data-test-held-funds-summary-limit>{{this.limitAmount}}</dd>
            </div>
          {{/if}}
          {{#if this.usedElsewhere}}
            <div>
              <dt>{{t "common.tas.approval.held_funds.summary.used"}}</dt>
              <dd data-test-held-funds-summary-used>{{this.usedElsewhere}}</dd>
            </div>
          {{/if}}
          <div>
            <dt>{{t "common.tas.approval.held_funds.summary.held"}}</dt>
            <dd class="tio-fg-warning" data-test-held-funds-summary-held>{{this.heldElsewhere}}</dd>
          </div>
          <div class="held-funds-summary__remaining">
            <dt>{{t "common.tas.approval.held_funds.summary.remaining"}}</dt>
            <dd
              class={{if this.isOverLimit "tio-fg-danger"}}
              data-test-held-funds-summary-remaining
            >{{this.remaining}}</dd>
          </div>
        </dl>

        <div class="held-funds-grid held-funds-columns" aria-hidden="true">
          <span>{{t "common.tas.approval.held_funds.column.application"}}</span>
          <span>{{t "common.tas.approval.held_funds.column.held_since"}}</span>
          <span class="text-right">{{t "common.tas.approval.held_funds.column.amount"}}</span>
          <span></span>
        </div>

        <ul class="held-funds-cards">
          {{#each this.groups as |group|}}
            <li
              class="held-funds-card {{this.cardClass group.rows}}"
              data-test-held-funds-application={{group.applicationId}}
            >
              <div class="held-funds-card__header">
                <div class="tio-cluster">
                  <span class="tio-text-sm-semibold tio-fg-primary">{{this.applicationName
                      group
                    }}</span>
                  <span
                    class="tio-text-xs-normal tio-fg-muted font-mono"
                  >#{{group.applicationId}}</span>
                  <Chip
                    @intent={{this.stateIntent group.applicationState}}
                    @size="sm"
                    data-test-held-funds-application-state={{this.stateIntent
                      group.applicationState
                    }}
                  >{{this.applicationStateLabel group.applicationState}}</Chip>
                  {{#if (this.isCourseCashApplication group.rows)}}
                    <Chip @intent="brand" @size="sm" data-test-held-funds-course-cash>{{t
                        "common.tas.approval.held_funds.course_cash_program"
                      }}</Chip>
                  {{/if}}
                </div>
                {{#if group.programName}}
                  <div class="tio-text-xs-normal tio-fg-muted mt-1">{{group.programName}}</div>
                {{/if}}
              </div>
              {{#each group.rows as |row|}}
                <div
                  class="held-funds-grid held-funds-card__row
                    {{unless row.reducible 'held-funds-card__row--locked'}}"
                  data-test-held-funds-row={{row.assistanceId}}
                >
                  <div>
                    <div class="tio-text-sm-medium tio-fg-primary">{{this.mannerLabel row}}</div>
                    <div
                      class="tio-text-xs-normal tio-fg-muted"
                      data-test-held-funds-hold-state
                    >{{this.holdStateLabel row}}</div>
                  </div>
                  <div class="tio-text-sm-normal tio-fg-secondary">{{this.heldOn row}}</div>
                  <div class="text-right">
                    <div
                      class="tio-text-sm-semibold tio-fg-primary"
                      data-test-held-funds-row-amount
                    >{{formatAsDollars row.amount}}</div>
                    {{#if (this.wasReduced row)}}
                      <div
                        class="tio-text-xs-normal tio-fg-success-body"
                        data-test-held-funds-row-reduced
                      >
                        {{t
                          "common.tas.approval.held_funds.reduced_from"
                          amount=(this.reducedFrom row)
                        }}
                      </div>
                    {{/if}}
                  </div>
                  <div class="text-right">
                    {{#if row.reducible}}
                      {{#unless (this.isEditing row)}}
                        <Button
                          @appearance="outlined"
                          @size="sm"
                          @isDisabled={{this.isReduceBlocked}}
                          @ariaLabel={{this.reduceLabel row}}
                          @testHook="held-funds__reduce"
                          data-test-held-funds-reduce
                          {{this.focusWhen (this.isReturnTarget row)}}
                          {{on "click" (fn this.startEditing row)}}
                        >
                          {{t "common.tas.approval.held_funds.reduce"}}
                        </Button>
                      {{/unless}}
                    {{else}}
                      <span
                        class="held-funds-card__blocked tio-text-xs-normal tio-fg-muted"
                        data-test-held-funds-blocked
                      >
                        <PhLock @size={{12}} aria-hidden="true" />
                        {{this.blockedReason row}}
                      </span>
                    {{/if}}
                  </div>
                </div>
                {{#if (this.isEditing row)}}
                  <div class="held-funds-card__editor tio-col" data-test-held-funds-editor>
                    <div class="held-funds-editor__amounts">
                      <Input
                        @label={{t "common.tas.approval.held_funds.reduce_by"}}
                        @value={{this.reductionInput}}
                        @onChange={{this.setReduction}}
                        @inputmode="decimal"
                        @placeholder="0.00"
                        @errors={{this.amountErrors}}
                        @isRequired={{true}}
                        @isDisabled={{this.isConfirming}}
                        @testHook="held-funds__reduction"
                        data-test-held-funds-reduction
                        {{this.focusWhen (not this.isConfirming)}}
                      />
                      <div class="held-funds-editor__figure">
                        <div class="tio-text-xs-medium tio-fg-muted">{{t
                            "common.tas.approval.held_funds.current_amount"
                          }}</div>
                        <div
                          class="tio-text-lg-semibold tio-fg-secondary"
                          data-test-held-funds-current-amount
                        >{{this.currentAmountLabel}}</div>
                      </div>
                      <div
                        class="held-funds-editor__figure
                          {{if this.newAmountLabel 'held-funds-editor__figure--new'}}"
                      >
                        <div class="tio-text-xs-medium tio-fg-muted">{{t
                            "common.tas.approval.held_funds.new_amount"
                          }}</div>
                        <div
                          class="tio-text-lg-semibold tio-fg-primary"
                          data-test-held-funds-new-amount
                        >{{if this.newAmountLabel this.newAmountLabel "—"}}</div>
                      </div>
                    </div>
                    <Textarea
                      @label={{t "common.tas.approval.held_funds.reason"}}
                      @hint={{t "common.tas.approval.held_funds.reason_hint"}}
                      @value={{this.reasonInput}}
                      @onChange={{this.setReason}}
                      @rows={{2}}
                      @errors={{this.reasonErrors}}
                      @isRequired={{true}}
                      @isDisabled={{this.isConfirming}}
                      data-test-held-funds-reason
                    />
                    {{#if this.saveError}}
                      <div
                        class="tio-text-sm-normal tio-fg-danger"
                        role="alert"
                        data-test-held-funds-error
                      >
                        {{this.saveError}}
                      </div>
                    {{/if}}
                    {{#if this.isConfirming}}
                      <div
                        class="held-funds-editor__confirm"
                        role="group"
                        aria-labelledby="held-funds-confirm-message"
                        data-test-held-funds-confirm
                      >
                        <p
                          id="held-funds-confirm-message"
                          class="tio-text-sm-medium tio-fg-primary"
                        >{{this.confirmMessage}}</p>
                        {{#if this.lowersCardSpend}}
                          <p class="tio-text-sm-normal tio-fg-secondary mt-1">{{t
                              "common.tas.approval.held_funds.confirm_card_note"
                            }}</p>
                        {{/if}}
                        <div class="tio-row-snug justify-end mt-3">
                          <Button
                            @appearance="outlined"
                            @size="sm"
                            @isDisabled={{this.save.isRunning}}
                            @testHook="held-funds__back"
                            data-test-held-funds-back
                            {{on "click" this.backToEditing}}
                          >
                            {{t "common.tas.approval.held_funds.back"}}
                          </Button>
                          <Button
                            @intent="primary"
                            @size="sm"
                            @isRunning={{this.save.isRunning}}
                            @isDisabled={{@isRefreshing}}
                            @testHook="held-funds__confirm"
                            data-test-held-funds-confirm-save
                            {{this.focusWhen true}}
                            {{on "click" this.save.perform}}
                          >
                            {{t "common.tas.approval.held_funds.confirm"}}
                          </Button>
                        </div>
                      </div>
                    {{else}}
                      <div class="tio-row-snug justify-end">
                        <Button
                          @appearance="outlined"
                          @size="sm"
                          @testHook="held-funds__cancel"
                          data-test-held-funds-cancel
                          {{on "click" this.cancelEditing}}
                        >
                          {{t "common.cancel"}}
                        </Button>
                        <Button
                          @intent="primary"
                          @size="sm"
                          @isDisabled={{this.isSaveBlocked}}
                          @testHook="held-funds__save"
                          data-test-held-funds-save
                          {{on "click" this.review}}
                        >
                          {{t "common.tas.approval.held_funds.save"}}
                        </Button>
                      </div>
                    {{/if}}
                  </div>
                {{/if}}
              {{/each}}
            </li>
          {{/each}}
        </ul>
      </:body>
      <:footer>
        <div class="tio-row-between gap-4">
          <p class="tio-text-xs-normal tio-fg-muted">
            {{t "common.tas.approval.held_funds.footer_note"}}
          </p>
          <Button
            @appearance="outlined"
            @testHook="held-funds__done"
            data-test-held-funds-done
            {{on "click" this.close}}
          >
            {{t "common.tas.approval.held_funds.done"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}

export default HeldFundsModal;
